import React, { useState } from "react";
import { Container, Row, Col, Form, Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import { Mail, ArrowLeft, ShieldAlert } from "lucide-react";
import userAPI from "../../api/UserApi";
import useAlphabeticInput from "../../hooks/useAlphabeticInput";
import { handleApiValidationError } from "../../utils/apiErrorHandler";

function ForgotPasswordCanvas() {
  useAlphabeticInput();

  const INITIAL_FORM_DATA = {
    email: "",
  };

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState({});
  const [general, setGeneral] = useState("");
  const [loading, setLoading] = useState(false);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isValidEmail = emailRegex.test(formData.email);

  const validate = () => {
    const newErrors = {};
    if (!formData.email) {
      newErrors.email = "Please Enter Email Address";
    } else if (!isValidEmail) {
      newErrors.email = "Please enter a valid email";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setGeneral("");

    if (!validate()) {
      setLoading(false);
      return;
    }

    try {
      const res = await userAPI.forgotPassword(formData);
      if (res.code === 200) {
        setGeneral(res.message || "Reset link dispatched to your email repository!");
        setFormData(INITIAL_FORM_DATA);
      }
    } catch (err) {
      handleApiValidationError(err, setErrors);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="orangeapes-login-page-container">
        <Container fluid className="h-100 p-0">
          <Row className="h-100 g-0">
            
            {/* 🌟 LEFT SIDE: TEXT-BASED EDITORIAL MANIFESTO PANEL (IMAGE-FREE) */}
            <Col lg={6} className="d-none d-lg-block orangeapes-login-text-manifesto-panel">
              <div className="manifesto-content-box">
                <div className="manifesto-top-branding">
                  <span>EST. 2025</span>
                  <h2>ORANGEAPES</h2>
                </div>

                <div className="manifesto-middle-perks-grid">
                  <div className="perk-text-row d-flex gap-3 align-items-start mb-4">
                    <ShieldAlert size={20} className="perk-icon-accent mt-1" />
                    <div>
                      <h6>Credential Verification</h6>
                      <p>Lost access to your vault metrics? Enter your authenticated user email address to trigger our strict automated recovery systems pipeline safely.</p>
                    </div>
                  </div>
                </div>

                <div className="manifesto-footer-copy">
                  <p>&copy; 2026 Orangeapes Studio. Engineered with precise structural minimalism.</p>
                </div>
              </div>
            </Col>

            {/* RIGHT SIDE: MINIMALIST FORGOT PASSWORD FORM CARD */}
            <Col
              lg={6}
              className="d-flex justify-content-center align-items-center p-4 p-md-5 bg-white"
            >
              <div className="orangeapes-login-form-card">
                
                <div className="form-header-typography mb-5">
                  <h1 className="orangeapes-login-main-title mb-2">Recover Access</h1>
                  <p className="orangeapes-login-subtitle">
                    Enter your registered email address below, and we will dispatch a dynamic secure token reset link right to your inbox catalog.
                  </p>
                </div>

                {/* SUCCESS MESSAGE ACTION BOX INDICATOR */}
                {general && (
                  <div className="orangeapes-input-micro-note-txt p-3 bg-light border-start border-warning border-3 mb-4 rounded-0">
                    🎉 <b>Vault Notice:</b> {general}
                  </div>
                )}

                <Form method="post" onSubmit={handleSubmit} className="orangeapes-form-element">
                  
                  {/* EMAIL INPUT FIELD */}
                  <Form.Group className="orangeapes-input-group-row mb-5" controlId="email">
                    <Form.Label className="orangeapes-form-custom-label">
                      Email Address <span className="text-orange-token">*</span>
                    </Form.Label>
                    <div className="orangeapes-field-input-wrapper">
                      <Mail size={16} className="orangeapes-field-icon" />
                      <Form.Control
                        type="email"
                        name="email"
                        placeholder="name@example.com"
                        value={formData.email}
                        onChange={handleChange}
                        className="orangeapes-blank-input"
                        disabled={loading}
                      />
                    </div>
                    {errors && errors.email && (
                      <span className="orangeapes-error-validation-msg">{errors.email}</span>
                    )}
                  </Form.Group>

                  {/* PREMIUM BUTTON SUBMIT RECOVERY */}
                  <Button
                    className="orangeapes-login-submit-btn-solid w-100 py-3 d-flex align-items-center justify-content-center gap-2"
                    type="submit"
                    disabled={loading}
                  >
                    <span>{loading ? "SENDING VERIFICATION..." : "SEND RESET LINK"}</span>
                  </Button>

                  {/* BACK TO LOGIN ANCHOR FOOTER TERMINAL */}
                  <div className="text-center mt-5 pt-3 orangeapes-form-footer-border">
                    <p className="orangeapes-register-redirect-txt">
                      Remember your password?
                      <Link
                        to="/users/login"
                        className="orangeapes-bold-redirect-anchor ms-2 d-inline-flex align-items-center justify-content-center gap-1"
                      >
                        <ArrowLeft size={13} /> Back to Login
                      </Link>
                    </p>
                  </div>
                </Form>

              </div>
            </Col>

          </Row>
        </Container>
      </div>
    </>
  );
}

export default ForgotPasswordCanvas;